export const exportToExcel = async (rows: any[], filename: string) => {
  if (!rows || rows.length === 0) return;
  const XLSX = await import('xlsx');
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

export const exportToPDF = async (columns: string[], data: any[][], filename: string, language: string = 'en', title?: string) => {
  if (!data || data.length === 0) return;
  const jsPDFModule = await import('jspdf');
  const autoTableModule: any = await import('jspdf-autotable');
  const jsPDF = (jsPDFModule as any).default || (jsPDFModule as any).jsPDF || jsPDFModule;
  const autoTable = autoTableModule.default || autoTableModule;
  const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation for better table display

  // Check if data contains Arabic text (check all cells)
  const containsArabic = data.some(row =>
    row.some(cell =>
      typeof cell === 'string' && /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(cell)
    )
  );

  // Load Arabic font if language is Arabic OR if data contains Arabic text
  let fontLoaded = false;
  if (language === 'ar' || containsArabic) {
    const { loadArabicFont } = await import('./pdfArabicFont');
    fontLoaded = await loadArabicFont(doc);
  }
  
  // Header Section
  doc.setFillColor(25, 118, 210);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 30, 'F');
  
  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  // Set Arabic font for title if language is Arabic
  if (language === 'ar' && fontLoaded) {
    const fontList = doc.getFontList();
    if (fontList && fontList['Amiri']) {
      doc.setFont('Amiri', 'normal');
    } else {
      doc.setFont('helvetica', 'bold');
    }
  } else {
    doc.setFont('helvetica', 'bold');
  }
  // Use provided title or generate from filename
  const pdfTitle = title || filename.charAt(0).toUpperCase() + filename.slice(1).replace(/_/g, ' ');
  doc.text(
    pdfTitle,
    doc.internal.pageSize.getWidth() / 2,
    18,
    { align: 'center' }
  );
  
  // Report Date
  doc.setFontSize(10);
  // Set Arabic font for date if language is Arabic
  if (language === 'ar' && fontLoaded) {
    const fontList = doc.getFontList();
    if (fontList && fontList['Amiri']) {
      doc.setFont('Amiri', 'normal');
    } else {
      doc.setFont('helvetica', 'normal');
    }
  } else {
    doc.setFont('helvetica', 'normal');
  }
  // Simple translation map for common strings
  const translations: { [key: string]: { [lang: string]: string } } = {
    generatedOn: { en: 'Generated on:', ar: 'تم الإنشاء في:' },
    totalRecords: { en: 'Total Records:', ar: 'إجمالي السجلات:' },
  };
  const generatedDate = new Date().toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  doc.text(
    `${translations.generatedOn[language] || translations.generatedOn.en} ${generatedDate}`,
    doc.internal.pageSize.getWidth() / 2,
    25,
    { align: 'center' }
  );
  
  // Summary Info
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  // Set Arabic font for summary if language is Arabic
  if (language === 'ar' && fontLoaded) {
    const fontList = doc.getFontList();
    if (fontList && fontList['Amiri']) {
      doc.setFont('Amiri', 'normal');
    } else {
      doc.setFont('helvetica', 'bold');
    }
  } else {
    doc.setFont('helvetica', 'bold');
  }
  doc.text(`${translations.totalRecords[language] || translations.totalRecords.en} ${data.length}`, 10, 40);
  
  // Prepare table styles
  const tableStyles: any = {
    fontSize: 7,
    cellPadding: 2,
    overflow: 'linebreak',
    cellWidth: 'wrap',
    halign: 'center',
    valign: 'middle',
  };

  // Set Arabic font for table if language is Arabic or data contains Arabic text
  const fontList = doc.getFontList();
  const hasArabicFont = fontList && fontList['Amiri'];
  const useArabicFont = (language === 'ar' || containsArabic) && hasArabicFont;

  // Set font globally before autoTable if Arabic font needed
  if (useArabicFont) {
    doc.setFont('Amiri', 'normal');
    tableStyles.font = 'Amiri';
    tableStyles.fontStyle = 'normal';
    tableStyles.useUnicode = true;
  }

  // Prepare head styles with Arabic font if needed
  const headStyles: any = {
    fillColor: [25, 118, 210],
    textColor: [255, 255, 255],
    fontStyle: 'normal',
    halign: 'center',
    valign: 'middle',
    fontSize: 7,
  };

  if (useArabicFont) {
    headStyles.font = 'Amiri';
    headStyles.fontStyle = 'normal';
    headStyles.useUnicode = true;
  }

  autoTable(doc, {
    head: [columns],
    body: data,
    startY: 48,
    styles: tableStyles,
    headStyles: headStyles,
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { top: 48, left: 5, right: 5 },
    tableWidth: 'auto',
    didParseCell: (cellData: any) => {
      // Set Arabic font for header cells
      if (cellData.section === 'head' && useArabicFont) {
        cellData.cell.styles.font = 'Amiri';
        cellData.cell.styles.fontStyle = 'normal';
        // Also set it directly on the cell text properties
        if (cellData.cell.text && Array.isArray(cellData.cell.text)) {
          cellData.cell.text.forEach((text: any) => {
            if (text && typeof text === 'object') {
              text.font = 'Amiri';
            }
          });
        }
      }
      // Set Arabic font for body cells if needed
      if (cellData.section === 'body' && useArabicFont) {
        cellData.cell.styles.font = 'Amiri';
        cellData.cell.styles.fontStyle = 'normal';
      }
    },
    didDrawCell: (cellData: any) => {
      // Force set Arabic font when drawing cells
      if (useArabicFont) {
        doc.setFont('Amiri', 'normal');
      }
    },
  });
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
  }
  
  doc.save(`${filename}-${new Date().toISOString().split('T')[0]}.pdf`);
};

